import { useGetEntityInfoQuery } from '@/components/organization-selection/services/orgSelectionApi';
import { useOrgWatermarkAvailable } from '@/hooks/useOrgWatermarkAvailable';
import { hasWebhookEndpoints, watermarkSignal } from '@/services/entity-doc';
import { hasCustomWebhookEndpoints } from '@/services/entity-tree';
import { propertyPmsAccount, type PmsAccountRef } from '@/components/pms/services/pmsAccount';

import type { AssetsEntry } from './usePropertyAssets';

/** "Whose account is this?", answered once per workspace (DV-2849 PR 2). */
export interface WorkspaceAccount {
  /** Read-only context for the header. Null on the account entry (the /home
   *  shell already names the account) and when the property record has none. */
  name: string | null;
  hasWebhookEndpoints: boolean;
  watermarkAvailable: boolean;
}

/** The two property-record fields the answer is keyed on. */
export interface WorkspaceAccountSource {
  entity: string | null;
  entityUUID: string | null;
}

/**
 * On the account entry (/home), today's session answers from the same two
 * functions the components used to call themselves, so /home cannot change.
 *
 * On the global-orders entry, the PROPERTY's account, read from entity-service
 * without switching into it. `GET /entities?entityId=` is deliberately
 * unscoped and returns the ancestor-resolved `watermarkImage` asset-service
 * watermarks with (spec §2). While that read is loading, fails, or finds
 * nothing, the watermark fails open (asset-service refuses an unwatermarkable
 * download with a typed 409) and webhooks fail closed (Send stays disabled, as
 * before this PR).
 */
export function useWorkspaceAccount(
  entry: AssetsEntry,
  property: WorkspaceAccountSource | null
): WorkspaceAccount {
  const isInternalEntry = entry === 'global-orders';
  const sessionWatermarkAvailable = useOrgWatermarkAvailable();
  const entityUUID = isInternalEntry ? property?.entityUUID ?? null : null;
  // `currentData`, never `data`: when the argument changes, RTK keeps the
  // PREVIOUS argument's `data` until the new request settles, which would show
  // the last property's account for the next one.
  // `refetchOnMountOrArgChange`: this cache entry is shared. usePrepaySaveGate
  // holds getEntityInfo(parentEntityId) for a whole /home session without
  // refetching, and Switch Organization does not reset API state, so a cached
  // document can be hours old by the time the workspace mounts on it (the
  // DV-2757 lesson: a cached doc must not answer for a live question).
  const { currentData } = useGetEntityInfoQuery(entityUUID ?? '', {
    skip: !entityUUID,
    refetchOnMountOrArgChange: true
  });

  if (!isInternalEntry) {
    return {
      name: null,
      hasWebhookEndpoints: hasCustomWebhookEndpoints(),
      watermarkAvailable: sessionWatermarkAvailable
    };
  }

  const doc = currentData?.[0];
  return {
    name: property?.entity ?? null,
    hasWebhookEndpoints: hasWebhookEndpoints(doc?.integrations),
    watermarkAvailable: watermarkSignal(doc) !== false
  };
}

/**
 * DV-3345: whose PMS account this workspace's PMS reads name. The session's on
 * /home; the property's own on the Global Orders photos page, and never the
 * session's there, even when the tab has one (spec D7). Computed by
 * usePropertyAssets, which owns the PMS read and produces the property record
 * this module's hook reads, so the order of the two hooks forbids computing it
 * inside useWorkspaceAccount (spec §4.3 correction).
 */
export const workspacePmsAccount = (
  entry: AssetsEntry,
  entityUUID: string | null | undefined,
  sessionAccount: PmsAccountRef | null
): PmsAccountRef | null => (entry === 'global-orders' ? propertyPmsAccount(entityUUID) : sessionAccount);
